import { useAxios } from '@/hooks/useAxios';
import { getQueryKey } from '@/hooks/useQueryKeys';
import { queryClient } from '@/queryclient';
import { useMutation } from '@/utils/api';

/**
 * Tenant-deletion write hooks (Praetorian-Admin-only full-tenant purge).
 *
 * Contract (frozen, see architecture.md "RESOLVED API contract"):
 * - POST   /account/deletion       body {tenant, mode, graceWindowDays?, nameConfirmation}
 * - DELETE /account/deletion/{id}  cancel a requested/scheduled deletion (id = record key)
 *
 * All calls target the tenant via the request body / URL (NEVER the `account`
 * impersonation header), so they use `useAxios({ doNotImpersonate: true })`
 * which resolves to the non-impersonated axios instance (useAxios.tsx:30-32,
 * :96-97 — that instance never sets the `account` header). Backend refusals
 * (403 gate, 409 denylist/subtenant/name-mismatch/uncancellable) are surfaced
 * verbatim via `getErrorFromResponse: true`.
 */

export type DeletionMode = 'immediate' | 'scheduled';

export type DeletionState =
  | 'requested'
  | 'enqueued'
  | 'scheduled'
  | 'purging'
  | 'verifying'
  | 'completed'
  | 'failed'
  | 'cancelled';

/**
 * Mirror of the backend `model.TenantDeletion` JSON wire shape
 * (pkg/tabularium/model/tenantdeletion.go). Shared type imported by F2/F5.
 * The record identifier is `key`; the `{id}` path param is the URL-escaped key.
 */
export interface DeletionRecord {
  /** Owning partition; always the global system user ("global"). */
  username: string;
  /** Unique record key, e.g. `#tenantdeletion#acme@example.com#<ts>`. */
  key: string;
  /** Target tenant being deleted (== the customer account email). */
  tenant: string;
  /** Cognito sub captured at request time (re-validated before purge). */
  targetSub?: string;
  /** Operator who requested the deletion. */
  initiator: string;
  state: DeletionState;
  mode: DeletionMode;
  graceWindowDays: number;
  requestedAt: string;
  scheduledPurgeAt?: string;
  startedAt?: string;
  completedAt?: string;
  nameConfirmation?: string;
  /** Per-store deleted counts (dynamodb, neo4j, s3_files, ...). */
  counts?: Record<string, number>;
  /** Post-purge verification counts; all zero means clean. */
  residual?: Record<string, number>;
  /** Resumability checklist; finished stores are skipped on re-run. */
  storesDone?: Record<string, boolean>;
  attempts?: number;
  errors?: string[];
  /** Global-S3 key of the completion certificate JSON. */
  certificateKey?: string;
}

export interface CreateTenantDeletionPayload {
  tenant: string;
  mode: DeletionMode;
  /** Grace window in days [1,30]; omitted for immediate purge. */
  graceWindowDays?: number;
  /** Must exactly equal `tenant`; server re-verifies. */
  nameConfirmation: string;
}

interface DeletionResponse {
  deletion: DeletionRecord;
}

/**
 * Invalidate the cross-tenant deletion list so read hooks refetch after a
 * successful create/cancel mutation.
 *
 * Returns the invalidateQueries promise so the mutation stays pending until the
 * tenant-deletion list refetch settles (TanStack Query awaits onSuccess).
 */
function invalidateTenantDeletions() {
  return queryClient.invalidateQueries({
    queryKey: getQueryKey.getTenantDeletions(),
  });
}

/** POST /account/deletion → 201 {deletion}. */
export function useCreateTenantDeletion() {
  const axios = useAxios({ doNotImpersonate: true });

  return useMutation({
    defaultErrorMessage: 'Failed to request tenant deletion',
    getErrorFromResponse: true,
    mutationFn: async (payload: CreateTenantDeletionPayload) => {
      const { data } = await axios.post<DeletionResponse>(
        '/account/deletion',
        payload
      );
      return data.deletion;
    },
    onSuccess: invalidateTenantDeletions,
  });
}

/**
 * DELETE /account/deletion/{id} → 200 {deletion} (409 if not requested/scheduled).
 * `id` is the raw `DeletionRecord.key`; it is URL-escaped here.
 */
export function useCancelTenantDeletion() {
  const axios = useAxios({ doNotImpersonate: true });

  return useMutation({
    defaultErrorMessage: 'Failed to cancel tenant deletion',
    getErrorFromResponse: true,
    mutationFn: async (id: string) => {
      const { data } = await axios.delete<DeletionResponse>(
        `/account/deletion/${encodeURIComponent(id)}`
      );
      return data.deletion;
    },
    onSuccess: invalidateTenantDeletions,
  });
}
